import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  IconButton,
} from "@mui/material";
import { Collect } from "../../../../models/collect";
import { Expense } from "../../../../models/expense";
import { Info as InfoIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  maxWidth: 500,
  height: "80vh",
  maxHeight: 500,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 2,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

interface CollectBoxProps {
  open: boolean;
  handleClose: () => void;
  collect: Collect | null;
  expenses: Expense[];
  selectedExpenses: number[];
  onSelectedExpensesChange: (expenseIds: number[]) => void;
  setValue: (value: number, expenseId: number) => void;
  isEditable: boolean;
}

const CollectBox: React.FC<CollectBoxProps> = ({
  open,
  handleClose,
  collect,
  expenses,
  selectedExpenses,
  onSelectedExpensesChange,
  setValue,
  isEditable,
}) => {
  const [inputValues, setInputValues] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!open) {
      setInputValues({});
      return;
    }

    setInputValues(prev => {
      const updated: Record<number, string> = {};

      selectedExpenses.forEach(id => {
        if (prev[id] !== undefined) {
          updated[id] = prev[id];
          return;
        }

        const expense = expenses.find(exp => exp.id === id);
        if (expense && expense.value !== undefined && expense.value !== null && expense.value !== 0) {
          updated[id] = expense.value.toString();
        } else {
          updated[id] = "";
        }
      });

      const prevKeys = Object.keys(prev);
      const hasChanges =
        prevKeys.length !== selectedExpenses.length ||
        selectedExpenses.some(id => updated[id] !== prev[id]);

      return hasChanges ? updated : prev;
    });
  }, [open, selectedExpenses, expenses]);

  const getDisplayValue = (expense: Expense) => {
    if (inputValues[expense.id] !== undefined) {
      return inputValues[expense.id];
    }

    if (expense.value !== undefined && expense.value !== null && expense.value !== 0) {
      return expense.value.toString();
    }

    return "";
  };

  const handleValueChange = (
    expenseId: number,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;

    setInputValues(prev => ({ ...prev, [expenseId]: value }));

    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      setValue(0, expenseId);
      return;
    }

    const parsedValue = Number(trimmedValue);
    if (!Number.isNaN(parsedValue) && parsedValue >= 0) {
      setValue(parsedValue, expenseId);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Typography id="modal-title" variant="h6" component="h2">
            Ingresar Detalle de Gastos
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }} aria-label="cerrar">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 3, overflowY: "auto", flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Seleccionar Gastos
          </Typography>
          <FormGroup>
            <Grid container spacing={2}>
              {expenses.map((expense) => (
                <Grid item xs={6} key={expense.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedExpenses.includes(expense.id)}
                        onChange={() => {
                          const newSelectedExpenses = selectedExpenses.includes(expense.id)
                            ? selectedExpenses.filter((id) => id !== expense.id)
                            : [...selectedExpenses, expense.id];
                          onSelectedExpensesChange(newSelectedExpenses);
                        }}
                        disabled={!isEditable}
                      />
                    }
                    label={expense.name}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {selectedExpenses.length > 0 ? (
              selectedExpenses.map((expenseId) => {
                const expense = expenses.find((exp) => exp.id === expenseId);
                if (!expense) return null;

                return (
                  <Grid item xs={12} key={expense.id} container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <Typography variant="body2">{expense.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Valor"
                        placeholder="1000"
                        type="number"
                        required
                        size="small"
                        value={getDisplayValue(expense)}
                        onChange={(event) => handleValueChange(expense.id, event)}
                        inputProps={{ maxLength: 20, min: 0 }}
                        sx={{
                          "& .MuiInputBase-input": {
                            fontSize: "0.875rem",
                            padding: "8px",
                          },
                        }}
                        disabled={!isEditable}
                      />
                    </Grid>
                  </Grid>
                );
              })
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" width="100%" py={3}>
                <InfoIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="body1">No hay detalles de operación seleccionados</Typography>
              </Box>
            )}
          </Grid>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button variant="contained" onClick={handleClose}>
            Cerrar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CollectBox;
